import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { AuthProvider } from '@/context/AuthContext';
import { RealtimeProvider } from '@/context/RealtimeContext';
import { ToastProvider } from '@/context/ToastContext';
import App from '@/App';
import '@/styles/global.css';
// V4.9.11 — UI re-skin layer. Imported AFTER global.css so its token + structural
// overrides win by load order. Pure presentation; changes no app behaviour.
import '@/styles/theme.css';

const devtoolsEnabled = String(import.meta.env.VITE_ENABLE_DEVTOOLS ?? 'false') === 'true';

function Root() {
  const [Devtools, setDevtools] = React.useState<React.ComponentType | null>(null);

  React.useEffect(() => {
    if (!devtoolsEnabled) return;
    let mounted = true;
    import('@tanstack/react-query-devtools')
      .then((mod) => {
        if (!mounted) return;
        const Component = () => React.createElement(mod.ReactQueryDevtools, { initialIsOpen: false });
        setDevtools(() => Component);
      })
      .catch(() => {
        // devtools are optional; ignore failures
      });
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <React.StrictMode>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <AuthProvider>
              <RealtimeProvider>
                <App />
              </RealtimeProvider>
            </AuthProvider>
          </ToastProvider>
          {Devtools ? <Devtools /> : null}
        </QueryClientProvider>
      </BrowserRouter>
    </React.StrictMode>
  );
}

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root was not found in index.html');
}
// V4.9.13 — HMR-safe mount. Cache the root so a Vite hot-update that re-runs
// this module reuses the existing root instead of calling createRoot() on
// #root a second time (which logs the dev-only "already passed to createRoot"
// warning). In a production build there's no HMR, so this just runs once.
const rootHost = window as unknown as { __bdsReactRoot?: ReturnType<typeof ReactDOM.createRoot> };
const root = rootHost.__bdsReactRoot ?? (rootHost.__bdsReactRoot = ReactDOM.createRoot(rootEl));
root.render(<Root />);
